import React, { useState } from 'react';
import Card from '../UI/Card';
import { CONFIG } from '../../config';
import { Code, Database, Cpu, CheckCircle, ChevronDown, Briefcase, Server, Layout, Terminal } from 'lucide-react';

/**
 * Experience Card Component
 * Internally used to render expandable experience items
 */
const ExperienceCard = ({ exp }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <Card hover={false} className="border-l-4 border-l-purple-500 bg-white dark:bg-slate-800/50 transition-all duration-300 shadow-sm dark:shadow-none hover:shadow-md">
            <div className="cursor-pointer flex justify-between items-start" onClick={() => setExpanded(!expanded)}>
                <div>
                    <h4 className="text-slate-900 dark:text-white font-bold text-lg">{exp.title}</h4>
                    <p className="text-purple-600 dark:text-purple-400 text-sm font-medium mb-1">{exp.company} {exp.type ? `• ${exp.type}` : ''}</p>
                    <p className="text-slate-500 dark:text-slate-400 text-xs">{exp.period}</p>
                </div>
                <div className={`p-2 bg-gray-50 dark:bg-white/5 rounded-full text-slate-500 dark:text-slate-400 transition-transform duration-300 ${expanded ? 'rotate-180' : 'rotate-0'}`}>
                    <ChevronDown size={18} />
                </div>
            </div>
            
            <div className={`grid transition-all duration-300 ease-in-out ${expanded ? 'grid-rows-[1fr] mt-4 opacity-100' : 'grid-rows-[0fr] mt-0 opacity-0'}`}>
                <div className="overflow-hidden">
                    <p className="mb-4 text-slate-700 dark:text-slate-300 text-sm leading-relaxed border-t border-gray-100 dark:border-white/5 pt-4">
                        {exp.description}
                    </p>
                    {exp.detailedDescription && exp.detailedDescription.length > 0 && (
                        <ul className="space-y-2 text-slate-600 dark:text-slate-400 text-sm">
                            {exp.detailedDescription.map((detail, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0"></span>
                                    <span>{detail}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </Card>
    );
};

/**
 * About Section Component
 * 
 * Displays the biography, specializations, experience timeline, and skills matrix.
 * Uses a grid layout to separate the bio/experience from the skills visual.
 */
const About = () => {
    return (
        <section className="py-12 md:py-20 px-4 sm:px-10 animate-in fade-in duration-500">
            <div className="max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">

                    {/* Left Column: Bio & Specializations */}
                    <div className="space-y-10 animate-in slide-in-from-bottom-8 duration-700">
                        {/* Bio */}
                        <div>
                            <div className="flex items-center gap-2 mb-6">
                                <span className="h-px w-8 bg-purple-500"></span>
                                <span className="text-purple-600 dark:text-purple-400 font-semibold uppercase tracking-wider text-sm">About Me</span>
                            </div>
                            <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">
                                Transforming ideas into <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400">digital reality</span>
                            </h2>
                            <div className="prose prose-invert prose-lg text-slate-600 dark:text-slate-400">
                                <p className="leading-relaxed">
                                    I am a dedicated and self-motivated .NET Developer with a strong passion for building
                                    efficient, secure, and scalable applications.
                                </p>
                                <p className="leading-relaxed mt-4">
                                    Currently working as a Software Developer & QA/QC Analyst at DreamIT in Sulaymaniyah, I contribute across Windows application development, full-stack website engineering, ERP module analysis, and software quality assurance.
                                </p>
                            </div>
                        </div>

                        {/* Specializations */}
                        <div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Specialized In</h3>
                            <div className="flex flex-wrap gap-3">
                                {CONFIG.specializations.map((spec, i) => (
                                    <div key={i} className="flex items-center gap-2 px-4 py-2 bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 rounded-full text-purple-700 dark:text-purple-300 text-sm shadow-sm dark:shadow-none hover:scale-105 transition-transform">
                                        <CheckCircle size={14} />
                                        {spec}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Experience Section */}
                        <div>
                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                                <Briefcase className="text-purple-600 dark:text-purple-500" />
                                Professional Experience
                            </h3>
                            <div className="space-y-4">
                                {CONFIG.experience.map((exp) => (
                                    <ExperienceCard key={exp.id} exp={exp} />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Skills Matrix Visual */}
                    <div className="space-y-8 animate-in slide-in-from-right-8 duration-700 delay-150">
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 blur-3xl rounded-full"></div>
                            <div className="relative bg-white/80 dark:bg-slate-900/50 backdrop-blur-xl border border-gray-200 dark:border-white/10 p-8 rounded-3xl space-y-8 shadow-xl dark:shadow-none transition-all hover:border-purple-500/30">
                                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                                    <Cpu className="text-purple-600 dark:text-purple-500" />
                                    Technical Arsenal
                                </h3>

                                <div className="space-y-8">
                                    {[
                                        { title: "Languages", icon: Code, skills: CONFIG.skills.languages },
                                        { title: "Backend Development", icon: Server, skills: CONFIG.skills.backend },
                                        { title: "Frontend Technologies", icon: Layout, skills: CONFIG.skills.frontend },
                                        { title: "Databases", icon: Database, skills: CONFIG.skills.databases },
                                        { title: "Tools & Technologies", icon: Terminal, skills: CONFIG.skills.tools },
                                        { title: "IDE & Tools & Agents", icon: Code, skills: CONFIG.skills.ide },
                                        { title: "Other Skills", icon: CheckCircle, skills: CONFIG.skills.other }
                                    ].map((group, idx) => {
                                        const Icon = group.icon;
                                        return (
                                            <div key={idx} className="space-y-3">
                                                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-medium">
                                                    <Icon size={18} className="text-purple-600 dark:text-purple-500" />
                                                    {group.title}
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {group.skills && group.skills.map(skill => (
                                                        <span key={skill} className="px-3 py-1.5 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/5 rounded-lg text-slate-600 dark:text-slate-300 text-xs hover:text-purple-700 dark:hover:text-white hover:border-purple-300 dark:hover:border-purple-500/30 hover:bg-purple-50 dark:hover:bg-purple-500/10 transition-all cursor-default shadow-sm hover:-translate-y-0.5">
                                                            {skill}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default About;
